define((require) => {
    "use strict";

    const ImprovedView              = require("editor/zbone/ImprovedView");
    const Tooltip                   = require("ui/common/Tooltip");
    const Syncer                    = require("shared/services/sync/Syncer");
    const EmailSliceModel           = require("ui/editor/email/EmailSliceModel");
    const PublishableState          = require("util/PublishableState");
    const WidgetValidator           = require("editor/services/WidgetValidator");

    const TOOLTIPS = {
        PROCESSING: "Please wait... Preparing email HTML.",
        MULTIPLE_SCENES_MODE: "We can not create email for Experience with multiple scenes.",
        NOT_EMAIL_MODE: "Turn on email mode first",
        PROCESSING_EXPERIENCE: "Please wait... Processing experience",
        PROCESSING_EXPERIENCE_FAILED: "Something went wrong... Please try to push experience again.",
        NOT_ALLOWED_WIDGETS: "Your experience contains widgets that cannot be used in emails",
        NOT_PUSHED: "Push your experience to be able to send a test email",
        DEFAULT: "This allows you to quickly send a test version of your email experience to yourself and team.\nTo insure the most up to date version of your email gets sent, remember to <b>push all your changes</b> before hitting \"Send Test Email\"",
        OVERLAPPED_WIDGETS: "You have overlapping interactive elements on your experience (highlighted on the canvas for you). Please separate them to be able to Send Test Email."
    };

    /**
     * Component representing the Email related buttons (SendTestEmailButtonView, CopyEmailButtonView)
     * for the <tt>EditorPreviewMenu</tt>.
     *
     * @cfg {experienceModel} model The current experience model.
     * @cfg {viewModel} model The current view model.
     *
     * @see EditorPreviewMenu
     *
     * @class AbstractEmailButtonView
     */
    return ImprovedView.extend({
        isPushedOnInitialize: null,
        experienceModel: null,
        viewModel: null,
        emailSliceModel: null,
        allIncompatibleWidgets: [],

        ui: {
            popup: ".popup",
            emailBtn: ".email-btn",
            emailBtnDefault: ".email-btn-default",
            emailBtnOmetria: ".email-btn-ometria",
            emailBtnKlaviyo: ".email-btn-klaviyo",
            emailBtnSFMC: ".email-btn-sfmc"
        },

        initialize() {
            this.tooltips = Object.assign({}, TOOLTIPS);
            this.viewModel = this.options.viewModel;
            this.experienceModel = this.options.viewModel.get("experience");
            this.model = new EmailSliceModel({
                _experience: this.experienceModel,
                _viewModel: this.viewModel
            });
            this.isPushedOnInitialization = this.experienceModel.hasAtLeastState(PublishableState.PUSHED);

            this.listenTo(this.viewModel, "change:isProcessingEmail change:hasOverlappedEmailWidgets", this._updateButtonState);
            this.listenTo(this.viewModel, "nestedWidgetTabOrderMetaDataChange", this._updateEmailEnableBtn);
            this.listenTo(this.experienceModel, "change:emailModeEnabled", this._updateEmailEnableBtn);
            this.listenTo(this.experienceModel, "add:scenes remove:scenes", this._updateListeners);
            this.listenTo(this.experienceModel.get("overlayScene").attributes.widgets, "add remove", this._updateEmailEnableBtn);
            this.listenTo(this.experienceModel.get("backgroundScene").attributes.widgets, "add remove", this._updateEmailEnableBtn);
            this.listenTo(this.experienceModel, "sync:state", (state, method) => {
                if (method !== "destroy") {
                    this._updateButtonState();
                }
                if (method === "pushed" && state === Syncer.State.Synced) {
                    this.isPushedOnInitialization = true;
                }
            });
            this._updateListeners();
        },

        _updateListeners() {
            this.experienceModel.get("scenes").models.forEach((model) => {
                this.listenTo(model.attributes.widgets, "add remove", this._updateEmailEnableBtn);
            });
            this._updateEmailEnableBtn();
        },

        _updateEmailEnableBtn() {
            setTimeout(() => {
                let widgetModelsToCheckAllScenes = [];
                this.experienceModel.get("scenes").models.forEach((model) => {
                    widgetModelsToCheckAllScenes = [...widgetModelsToCheckAllScenes, ...model.attributes.widgets.models];
                });
                const widgetModelsToCheckBackground = this.experienceModel.get("backgroundScene").attributes.widgets.models;
                const widgetModelsToCheckOverlay = this.experienceModel.get("overlayScene").attributes.widgets.models;

                const widgetModelsToCheck = [...widgetModelsToCheckAllScenes, ...widgetModelsToCheckBackground, ...widgetModelsToCheckOverlay];
                this.allIncompatibleWidgets = WidgetValidator.getActiveIncompatibleEmailWidgets({
                    widgetModelsToCheck,
                    systemMessage: "This experience contains widgets which cannot be used in emails: . Please remove these widgets before continuing.",
                    isEmailMode: this.experienceModel && this.experienceModel.get("emailModeEnabled"),
                    viewModel: this.viewModel
                });
                this._updateButtonState();
            }, 200);
        },

        onRender: function() {
            this._updateButtonState();
        },

        onClose() {
            Tooltip.remove(this.ui.emailBtn);
        },

        onShow() {
            this._updateEmailEnableBtn();
        },

        _getCurrentStates() {
            const isProcessingEmail = this.viewModel.get("isProcessingEmail");
            const hasOverlappedEmailWidgets = this.viewModel.get("hasOverlappedEmailWidgets");
            const isEmailMode = this.experienceModel.get("emailModeEnabled");
            const isPushed = this.experienceModel.hasAtLeastState(PublishableState.PUSHED);
            const hasMultipleScenes = this.experienceModel.get("scenes").length > 1;
            const hasIncompatibleWidgets = !!this.allIncompatibleWidgets.length;

            const pending = Syncer.State.current(this.experienceModel) === Syncer.State.Pending;
            const syncing = Syncer.State.current(this.experienceModel) === Syncer.State.Syncing;
            return {
                isEmailMode, isProcessingEmail, hasIncompatibleWidgets, hasOverlappedEmailWidgets, isPushed, hasMultipleScenes, pending, syncing
            };
        },

        _getTooltipText() {
            const {
                isEmailMode,
                isPushed,
                hasMultipleScenes,
                pending,
                syncing,
                isProcessingEmail,
                hasIncompatibleWidgets,
                hasOverlappedEmailWidgets
            } = this._getCurrentStates();

            let tooltipText = this.tooltips.DEFAULT;
            if (!isEmailMode) {
                tooltipText = this.tooltips.NOT_EMAIL_MODE;
            } else if (hasOverlappedEmailWidgets) {
                tooltipText = this.tooltips.OVERLAPPED_WIDGETS;
            } else if (hasMultipleScenes) {
                tooltipText = this.tooltips.MULTIPLE_SCENES_MODE;
            } else if (hasIncompatibleWidgets) {
                const widgetsName = WidgetValidator.getIncompatibleWidgetsNames(this.allIncompatibleWidgets);
                tooltipText = `${this.tooltips.NOT_ALLOWED_WIDGETS}: ${widgetsName}.`;
            } else if (pending || syncing) {
                tooltipText = this.tooltips.PROCESSING_EXPERIENCE;
            } else if (!isPushed) {
                tooltipText = this.tooltips.NOT_PUSHED;
            } else if (isProcessingEmail) {
                tooltipText = this.tooltips.PROCESSING;
            }

            return tooltipText;
        },

        _getButtonState() {
            const {
                isEmailMode,
                hasOverlappedEmailWidgets,
                isPushed,
                hasMultipleScenes,
                changedAfterPush,
                pending,
                failed,
                syncing,
                isProcessingEmail,
                hasIncompatibleWidgets
            } = this._getCurrentStates();

            return !isEmailMode
                || hasOverlappedEmailWidgets
                || hasMultipleScenes
                || hasIncompatibleWidgets
                || !isPushed
                || changedAfterPush
                || pending
                || failed
                || isProcessingEmail
                || syncing;
        },

        _updateButtonState() {
            const tooltipText = this._getTooltipText();
            const disabled = this._getButtonState();

            this._updateTooltip(tooltipText);
            this.ui.emailBtn.toggleClass("disabled", disabled);
        },

        _updateTooltip(tooltipText) {
            Tooltip.remove(this.ui.emailBtn);
            let tooltipSettings = {
                width: 200,
                animation: false,
                trigger: "hover",
                placement: "left",
                duration: 0,
                content: tooltipText,
                html: true
            };
            Tooltip.add(this.ui.emailBtn, tooltipSettings);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/email/AbstractEmailButtonView.js